import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchBusinessDomain, updateBusinessDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function BusinessKpiListPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      setDomain(await fetchBusinessDomain(businessDomainId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [businessDomainId]);

  const handleDelete = async () => {
    try {
      const updated = { ...domain, indicators: domain.indicators.filter((_, i) => i !== targetIndex) };
      await updateBusinessDomain(updated);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      setTargetIndex(null);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };

  const indicators = domain?.indicators || [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("businessKpi.list.title")}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/design/${businessDomainId}/business-kpis/create`)}>
          {t("common.add")}
        </Button>
      </Box>
      {loading ? (
        <Typography color="text.secondary">{t("common.loading")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t("businessKpi.indicatorName")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("businessKpi.unit")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("common.description")}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 140 }}>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indicators.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" variant="body2">{t("businessKpi.empty")}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                indicators.map((ind, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{ind.indicatorName}</TableCell>
                    <TableCell>{ind.unit}</TableCell>
                    <TableCell>{ind.description}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => navigate(`/design/${businessDomainId}/business-kpis/${idx}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => navigate(`/design/${businessDomainId}/business-kpis/${idx}/edit`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => { setTargetIndex(idx); setConfirmOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => { setConfirmOpen(false); setTargetIndex(null); }} />
    </Box>
  );
}
